'use client'

import { Avatar, Button, Icon, IconComponent } from '@graftcode/design-system'

import { getSafeImageUrl } from '@common/core/helpers'
import type { Project } from '@common/modules/projects/types/project.types'

import { getInitials } from '../../../Sidebar/Sidebar.helpers'
import { useMobileMenuContext } from '../../context'

import styles from '../../MobileMenu.module.scss'

interface ProjectsViewProps {
  /** List of projects */
  projects: Project[]
  /** Currently selected project ID */
  currentProjectId?: string
  /** Handler for project selection */
  onProjectSelect: (projectId: string) => void
}

export function ProjectsView({ projects, currentProjectId, onProjectSelect }: ProjectsViewProps) {
  const {
    navigateToMain,
    onClose,
    onOpenProjectSettings,
    onOpenProjectMembers,
    onInviteProjectClick,
    onCreateProject,
  } = useMobileMenuContext()

  const handleSettingsClick = () => {
    onOpenProjectSettings?.()
    onClose()
  }

  const handleMembersClick = () => {
    onOpenProjectMembers?.()
    onClose()
  }

  const handleInviteClick = () => {
    onInviteProjectClick?.()
    onClose()
  }

  const handleCreateProjectClick = () => {
    onCreateProject?.()
    onClose()
  }

  return (
    <div className={styles.menuView}>
      {/* Header with back button */}
      <div className={styles.header}>
        <button className={styles.backButton} onClick={navigateToMain}>
          <IconComponent icon={Icon.ChevronLeftRegular} size={24} />
          <span>Select Project</span>
        </button>
        <div className={styles.headerActions}>
          <Button iconOnly size="l" variant="ghost" icon={Icon.DismissRegular} onClick={onClose} />
        </div>
      </div>

      {/* Projects List */}
      <div className={styles.content}>
        <div className={styles.submenuSection}>
          {/* Project Actions */}
          <div className={styles.submenuGroup}>
            <button
              className={`${styles.submenuItem} ${!onOpenProjectSettings ? styles.disabled : ''}`}
              onClick={handleSettingsClick}
              disabled={!onOpenProjectSettings}
            >
              <IconComponent icon={Icon.SettingsRegular} size={20} />
              <span>Settings</span>
            </button>
            <button
              className={`${styles.submenuItem} ${!onOpenProjectMembers ? styles.disabled : ''}`}
              onClick={handleMembersClick}
              disabled={!onOpenProjectMembers}
            >
              <IconComponent icon={Icon.PeopleRegular} size={20} />
              <span>Project members</span>
            </button>
            <button
              className={`${styles.submenuItem} ${!onInviteProjectClick ? styles.disabled : ''}`}
              onClick={handleInviteClick}
              disabled={!onInviteProjectClick}
            >
              <IconComponent icon={Icon.PeopleAddRegular} size={20} />
              <span>Invite people</span>
            </button>
          </div>

          {/* Separator */}
          <div className={styles.submenuSeparator} />

          {/* Projects List */}
          <div className={styles.submenuGroup}>
            <div className={styles.submenuGroupLabel}>Switch project</div>
            {projects.map((project) => {
              const isSelected = currentProjectId === project.id
              const initials = getInitials(project.name)
              return (
                <button
                  key={project.id}
                  className={`${styles.submenuItem} ${isSelected ? styles.selected : ''}`}
                  onClick={() => onProjectSelect(project.id)}
                >
                  <div>
                    <Avatar size={20} src={getSafeImageUrl(project.logo)} fallback={initials} />
                  </div>
                  <span>{project.name}</span>
                  {isSelected && <IconComponent icon={Icon.CheckmarkRegular} size={20} className={styles.checkmark} />}
                </button>
              )
            })}
          </div>

          {/* Separator */}
          <div className={styles.submenuSeparator} />

          {/* Create Project */}
          <div className={styles.submenuGroup}>
            <button
              className={`${styles.submenuItem} ${!onCreateProject ? styles.disabled : ''}`}
              onClick={handleCreateProjectClick}
              disabled={!onCreateProject}
            >
              <IconComponent icon={Icon.AddRegular} size={20} />
              <span>Create project</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
